import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  Grid,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, SectionHeader, SectionLoading } from './ui/Section';
import { ShareDonut } from './ui/charts';
import { format } from 'date-fns';
import { useAxiosClient } from '../utils/useAxiosClient';
import { formatTikTokLink } from '../utils/linkFormatting';

interface AudienceSentimentProps {
  selectedBrands: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
}

interface Comment {
  date: string;
  message: string;
  sentiment: string;
  post_link: string;
  likes: number;
}

interface SentimentDistribution {
  positive: number;
  neutral: number;
  negative: number;
}

const AudienceSentiment: React.FC<AudienceSentimentProps> = ({
  selectedBrands,
  selectedPillars,
  startDate,
  endDate
}) => {
  const axiosClient = useAxiosClient();
  const theme = useTheme();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [distribution, setDistribution] = useState<SentimentDistribution>({
    positive: 0,
    neutral: 0,
    negative: 0
  });
  const [totalComments, setTotalComments] = useState<number>(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Build query string
        const params = new URLSearchParams();
        
        if (selectedBrands.length > 0) {
          params.append('brands', selectedBrands[0]);
        }
        
        selectedPillars.forEach(pillar => {
          params.append('pillars', pillar);
        });
        
        if (startDate) {
          params.append('start_date', startDate);
        }
        
        if (endDate) {
          params.append('end_date', endDate);
        }
        
        // Make API call using axiosClient instead of axios
        const response = await axiosClient.get(`/api/audience-sentiment?${params.toString()}`);
        
        // Update state with response data
        setComments(response.data.comments || []);
        setDistribution(response.data.sentiment_distribution || { positive: 0, neutral: 0, negative: 0 });
        setTotalComments(response.data.total_comments || 0);
      } catch (err) {
        console.error('Error fetching audience sentiment data:', err);
        setError('Failed to load audience sentiment data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [axiosClient, selectedBrands, selectedPillars, startDate, endDate]);

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sentimentTone = (sentiment: string) =>
    sentiment === 'Positive' ? theme.ink.positive : sentiment === 'Negative' ? theme.ink.negative : theme.palette.text.secondary;

  // Prepare data for the distribution donut
  const shareData = [
    { name: 'Positive', count: distribution.positive },
    { name: 'Neutral', count: distribution.neutral },
    { name: 'Negative', count: distribution.negative },
  ]
    .filter(item => item.count > 0)
    .map(item => ({
      name: item.name,
      value: totalComments > 0 ? (item.count / totalComments) * 100 : 0,
      rawValue: item.count,
      color: item.name === 'Neutral' ? alpha(theme.palette.text.secondary, 0.45) : sentimentTone(item.name),
    }));

  // Format date
  const formatDate = (dateString: string) => {
    try {
      // Create date without timezone conversion
      const [year, month, day] = dateString.split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const header = (
    <SectionHeader
      title="Audience Sentiment"
      info="How commenters reacted to this person's posts. Each comment is classified as positive, neutral, or negative."
    />
  );

  if (loading) {
    return (
      <SectionCard>
        {header}
        <SectionLoading height={320} />
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard>
        {header}
        <Typography color="error" sx={{ py: 5, textAlign: 'center' }}>{error}</Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      {header}

      <Grid container spacing={2}>
        {/* Distribution */}
        <Grid item xs={12} md={5} lg={4}>
          {shareData.length > 0 ? (
            <ShareDonut
              title="Sentiment distribution"
              data={shareData}
              rawLabel="Comments"
              centerValue={totalComments.toLocaleString()}
              centerCaption="comments"
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              No sentiment data for these filters.
            </Typography>
          )}
        </Grid>

        {/* Comments table */}
        <Grid item xs={12} md={7} lg={8}>
          <TableContainer sx={(t) => ({ maxHeight: 460, border: `1px solid ${t.palette.divider}`, borderRadius: '10px' })}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'background.paper' } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell align="right">Likes</TableCell>
                  <TableCell>Link</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((comment, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatDate(comment.date)}</TableCell>
                      <TableCell sx={{ maxWidth: 0, width: '60%' }}>
                        <Tooltip title={comment.message} placement="top-start">
                          <Typography noWrap variant="body2">
                            {comment.message}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: sentimentTone(comment.sentiment),
                            bgcolor: alpha(sentimentTone(comment.sentiment), theme.palette.mode === 'dark' ? 0.18 : 0.1),
                          }}
                        >
                          {comment.sentiment}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{comment.likes}</TableCell>
                      <TableCell>
                        {comment.post_link ? (
                          <Link
                            href={formatTikTokLink(comment.post_link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                          >
                            View <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        ) : (
                          <Typography component="span" variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                {comments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No comments for these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={comments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export default AudienceSentiment; 